import {
  cn,
  createNonNullableContext,
  focusRingClassName,
  tv,
} from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { ChevronDownIcon } from 'lucide-react'
import { cloneElement, isValidElement } from 'react'
import type { ComponentProps, ReactElement, ReactNode, SVGProps } from 'react'

import { Badge } from './Badge'
import type { BadgeProps } from './Badge'
import { CheckboxRawIndicator } from './Checkbox'
import { RadioGroupRawIndicator } from './RadioGroup'
import { Slot } from './Slot'
import { SpinnerIcon } from './SpinningLoader'
import type { TooltipProps } from './Tooltip'
import { Tooltip } from './Tooltip'

export const buttonVariants = tv({
  base: [
    'group group/button relative inline-flex items-center justify-center rounded-md transition enabled:cursor-pointer border aria-invalid:border-red-500 aria-invalid:ring-red-500/20 data-[autofilled=true]:bg-tremor-brand-faint',
    focusRingClassName,
  ],

  compoundVariants: [
    {
      className:
        'bg-tremor-background-subtle hover:not-disabled:bg-tremor-background-subtle',
      isSelected: true,
      variant: 'outline',
    },
    {
      className: 'bg-tremor-background-subtle border-tremor-border',
      isSelected: true,
      variant: 'ghost',
    },
    {
      className: 'bg-yellow-100 dark:bg-yellow-900/50',
      isSelected: true,
      variant: 'brand',
    },
    {
      borderVariant: 'default',
      className: 'border-transparent',
      isSelected: false,
      variant: 'ghost',
    },
    {
      borderVariant: 'solid',
      className: 'border-tremor-border',
      variant: 'ghost',
    },
    {
      className: 'text-white',
      variant: 'destructive',
    },
    {
      // Inline buttons sit inside a text line, so they add no height; the hit area keeps a 24px target.
      className: 'hit-area-0.5 min-h-0 border-0 px-1 py-0',
      density: 'inline',
    },
  ],

  defaultVariants: {
    align: 'center',
    borderVariant: 'default',
    colorVariant: 'default',
    density: 'default',
    disabledInteractivity: 'none',
    isPending: false,
    isSelected: false,
    size: 'md',
    variant: 'outline',
    weight: 'medium',
    width: 'auto',
  },

  slots: {
    content: 'relative inline-flex min-w-0 items-center',
    count: 'ml-1.5 inline-flex shrink-0',
    endAdornment: 'ml-1.5 shrink-0',
    startAdornment: 'mr-1.5 shrink-0',
  },

  variants: {
    align: {
      between: {
        base: 'justify-between text-left',
        content: 'w-full justify-between text-left',
      },
      center: {
        base: 'justify-center',
        content: 'justify-center',
      },
      start: {
        base: 'justify-start text-left',
        content: 'items-start justify-start text-left',
      },
    },

    borderVariant: {
      dashed: 'border-dashed',
      default: null,
      solid: null,
    },

    colorVariant: {
      brand: 'text-yellow-600 dark:text-yellow-400',
      'brand-subtle': 'text-tremor-brand-subtle',
      default: 'text-tremor-content-strong',
      destructive: 'text-red-600 hover:not-disabled:text-red-700',
      emphasis: 'text-tremor-content-emphasis',
      muted: 'text-tremor-content',
      subtle: 'text-tremor-content-subtle',
    },

    density: {
      compact: 'px-1.5',
      default: null,
      inline: null,
    },

    disabledInteractivity: {
      none: 'disabled:pointer-events-none disabled:opacity-50',
      'opacity-pointer': 'disabled:pointer-events-auto',
      pointer: 'disabled:pointer-events-auto disabled:opacity-50',
    },

    isPending: {
      true: {
        content: 'animate-out fill-mode-forwards fade-out',
      },
    },

    isSelected: {
      true: null,
    },

    size: {
      md: {
        base: 'px-3 py-2 text-sm',
        endAdornment: 'size-4.5',
        startAdornment: 'size-4.5',
      },
      sm: {
        base: 'px-3 py-1 text-[0.8rem] min-h-8',
        content: 'leading-tight',
        endAdornment: 'size-4',
        startAdornment: 'size-4',
      },
      xs: {
        base: 'px-2 py-1 text-xs',
        count: '-my-0.5',
        endAdornment: 'size-3',
        startAdornment: 'size-3',
      },
    },

    variant: {
      brand: {
        base: 'border-yellow-500 bg-yellow-50 hover:not-disabled:bg-yellow-100 dark:bg-yellow-900/30 dark:border-yellow-600 data-[state=open]:bg-yellow-100 dark:data-[state=open]:bg-yellow-900/50 dark:hover:not-disabled:bg-yellow-900/50',
      },
      'brand-subtle': {
        base: 'border-tremor-brand-subtle bg-tremor-brand-subtle text-tremor-content-strong dark:text-tremor-content-inverted hover:not-disabled:brightness-95 dark:hover:not-disabled:brightness-110',
      },
      destructive: {
        base: 'bg-red-500 text-white hover:not-disabled:bg-red-600 border-red-500 hover:not-disabled:border-red-600 dark:bg-red-600 dark:border-red-600 dark:hover:not-disabled:bg-red-700 dark:hover:not-disabled:border-red-700',
      },
      flush: {
        base: 'rounded-none border-transparent bg-transparent shadow-none hover:not-disabled:bg-transparent hover:not-disabled:text-tremor-content-emphasis',
        content: 'min-w-0 w-full',
      },
      ghost: {
        base: 'bg-transparent hover:not-disabled:border-tremor-border hover:not-disabled:bg-tremor-background-subtle data-[state=open]:bg-tremor-background-subtle data-[state=open]:border-tremor-border',
      },
      'ghost-static': {
        base: 'border-transparent bg-transparent hover:not-disabled:border-tremor-border hover:not-disabled:bg-tremor-background-subtle',
      },
      glass: {
        base: 'border-white/60 bg-tremor-background/50 shadow-md backdrop-blur-md backdrop-saturate-150 hover:not-disabled:bg-tremor-background/75 data-[state=open]:bg-tremor-background/75 dark:border-white/10',
      },
      outline: {
        base: 'border-tremor-border bg-tremor-background shadow-xs hover:not-disabled:bg-tremor-background-muted data-[state=open]:bg-tremor-background-muted',
      },
      unstyled: {
        base: 'border-transparent',
      },
    },

    weight: {
      medium: 'font-medium',
      normal: 'font-normal',
    },

    width: {
      auto: null,
      fit: 'w-fit',
      full: {
        base: 'w-full',
        content: 'min-w-0 w-full',
      },
    },
  },
})

const BUTTON_COUNT_BADGE_VARIANT = {
  brand: 'brand',
  'brand-subtle': 'brand',
  destructive: 'error',
  flush: 'neutral',
  ghost: 'neutral',
  'ghost-static': 'neutral',
  glass: 'neutral',
  outline: 'neutral',
  unstyled: 'neutral',
} as const satisfies Record<
  NonNullable<VariantProps<typeof buttonVariants>['variant']>,
  BadgeProps['variant']
>

export interface ButtonProps
  extends
    Omit<ComponentProps<'button'>, 'className'>,
    VariantProps<typeof buttonVariants> {
  /**
   * @default false
   */
  readonly asChild?: boolean
  readonly count?: number

  /**
   * @default props.isPending
   */
  disabled?: boolean
  readonly endIcon?: ReactElement<unknown>
  readonly overlay?: ReactElement
  readonly startIcon?: ReactElement<unknown>
  readonly tooltip?: string | Omit<TooltipProps, 'children'>
}

export function Button({
  align,
  asChild = false,
  borderVariant,
  children,
  colorVariant,
  count: _count,
  density,
  disabled,
  disabledInteractivity,
  endIcon: _endIcon,
  isPending,
  isSelected,
  overlay,
  ref,
  size,
  startIcon: _startIcon,
  tooltip,
  variant,
  weight,
  width,
  ...buttonProps
}: ButtonProps) {
  const classNames = buttonVariants({
    align,
    borderVariant,
    colorVariant,
    density,
    disabledInteractivity,
    isPending,
    isSelected,
    size,
    variant,
    weight,
    width,
  })
  const Comp = asChild ? Slot : 'button'
  const startIcon = _startIcon ? (
    <Slot className={classNames.startAdornment()}>{_startIcon}</Slot>
  ) : null
  const count =
    _count !== undefined ? (
      <span className={classNames.count()}>
        <Badge
          rounding="full"
          size="sm"
          variant={BUTTON_COUNT_BADGE_VARIANT[variant ?? 'outline']}
        >
          {_count}
        </Badge>
      </span>
    ) : null
  const endIcon = _endIcon ? (
    <Slot className={classNames.endAdornment()}>{_endIcon}</Slot>
  ) : null
  const isDisabled = Boolean(disabled) || Boolean(isPending)
  const loader =
    overlay ??
    (isPending ? (
      <div className="animate-in fade-in absolute inset-0 flex items-center justify-center">
        <SpinnerIcon className="size-4" />
      </div>
    ) : null)
  const contentClassName = classNames.content()
  // SAFETY: Slot asChild injects className onto button props omitted from ButtonProps.
  const { className: slotForwardedClassName, ...safeButtonProps } =
    buttonProps as typeof buttonProps & { className?: string }

  const button = (
    <Comp
      ref={ref}
      data-slot="button"
      type="button"
      disabled={isDisabled}
      className={cn(classNames.base(), slotForwardedClassName)}
      {...safeButtonProps}
    >
      {asChild ? (
        isValidElement<{ children: ReactNode }>(children) ? (
          // oxlint-disable-next-line react/no-clone-element
          cloneElement(
            children,
            undefined,
            <span className={contentClassName}>
              {startIcon}
              {children.props.children}
              {count}
              {endIcon}
            </span>,
            loader,
          )
        ) : null
      ) : (
        <>
          <span className={contentClassName}>
            {startIcon}
            {children}
            {count}
            {endIcon}
          </span>
          {loader}
        </>
      )}
    </Comp>
  )

  return tooltip === undefined ? (
    button
  ) : (
    <Tooltip
      {...(typeof tooltip === 'string' ? { content: tooltip } : tooltip)}
    >
      {button}
    </Tooltip>
  )
}

export const iconButtonVariants = tv({
  base: ['min-w-0 shrink-0', focusRingClassName],

  compoundVariants: [
    {
      class: {
        base: 'p-1',
      },
      density: 'compact',
    },
  ],

  defaultVariants: {
    density: 'default',
    hasAdornment: false,
    size: 'md',
  },

  slots: {
    icon: 'shrink-0',
  },

  variants: {
    density: {
      compact: null,
      default: null,
      inline: null,
    },

    hasAdornment: {
      false: {
        base: 'aspect-square',
      },
    },

    size: {
      inline: {
        base: 'p-0 size-3.5 hit-area-2',
        icon: 'size-3',
      },
      lg: {
        base: 'p-1.5',
        icon: 'size-6',
      },
      md: {
        base: 'p-2',
        icon: 'size-5',
      },
      sm: {
        base: 'size-8',
        icon: 'size-4',
      },
      xl: {
        base: 'p-3',
        icon: 'size-6',
      },
      xs: {
        base: 'p-1 size-6.5 hit-area-1',
        icon: 'size-3.5',
      },
    },
  },
})

export interface IconButtonProps
  extends
    Omit<ButtonProps, 'startIcon' | 'endIcon' | 'size'>,
    Omit<VariantProps<typeof iconButtonVariants>, 'hasAdornment'> {
  readonly children: ReactElement<unknown>
  endAdornment?: ReactElement<unknown>

  /**
   * @default false
   */
  isPending?: boolean
}

export function IconButton({
  align,
  asChild = false,
  borderVariant,
  children,
  colorVariant,
  density,
  disabled: _disabled,
  disabledInteractivity,
  endAdornment,
  isPending,
  isSelected,
  ref,
  size,
  tooltip,
  variant,
  weight,
  width,
  ...props
}: IconButtonProps) {
  const Comp = asChild ? Slot : 'button'
  const buttonClassNames = buttonVariants({
    align,
    borderVariant,
    colorVariant,
    density,
    disabledInteractivity,
    isSelected,
    variant,
    weight,
    width,
  })
  const classNames = iconButtonVariants({
    hasAdornment: endAdornment !== undefined,
    size,
  })
  const contentClassName = cn(buttonClassNames.content(), 'gap-x-0.5')

  const button = (
    <Comp
      aria-label={
        typeof tooltip === 'string'
          ? tooltip
          : typeof tooltip?.content === 'string'
            ? tooltip.content
            : undefined
      }
      data-slot="button"
      type="button"
      {...props}
      ref={ref}
      className={cn(
        buttonClassNames.base(),
        classNames.base(),
        endAdornment ? 'aspect-auto' : null,
      )}
      disabled={Boolean(_disabled) || Boolean(isPending)}
    >
      {asChild ? (
        isValidElement<{ children: ReactNode }>(children) ? (
          // oxlint-disable-next-line react/no-clone-element
          cloneElement(
            children,
            undefined,
            <span className={contentClassName}>
              <Slot className={classNames.icon()}>
                {isPending ? <SpinnerIcon /> : children.props.children}
              </Slot>
              {endAdornment}
            </span>,
          )
        ) : null
      ) : (
        <span className={contentClassName}>
          <Slot className={classNames.icon()}>
            {isPending ? <SpinnerIcon /> : children}
          </Slot>
          {endAdornment}
        </span>
      )}
    </Comp>
  )

  return tooltip === undefined ? (
    button
  ) : (
    <Tooltip
      {...(typeof tooltip === 'string' ? { content: tooltip } : tooltip)}
    >
      {button}
    </Tooltip>
  )
}

const buttonGroupVariants = tv({
  base: 'grid items-center',

  defaultVariants: {
    density: 'normal',
    orientation: 'horizontal',
  },

  variants: {
    density: {
      compact: 'gap-y-1 gap-x-1',
      normal: 'gap-y-2 gap-x-1.5',
      spacious: 'gap-y-2.5 gap-x-2',
    },

    orientation: {
      horizontal: 'auto-cols-max grid-flow-col',
      vertical: 'w-full grid-flow-row auto-rows-auto',
    },
  },
})

export interface ButtonGroupProps
  extends ComponentProps<'div'>, VariantProps<typeof buttonGroupVariants> {
  /**
   * @default false
   */
  readonly asChild?: boolean
}

export function ButtonGroup({
  asChild = false,
  children,
  className,
  density,
  orientation = 'horizontal',
  ref,
  ...props
}: ButtonGroupProps) {
  const Component = asChild ? Slot : 'div'
  return (
    <Component
      ref={ref}
      className={cn(
        buttonGroupVariants({
          className,
          density,
          orientation,
        }),
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

const splitButtonGroupVariants = tv({
  base: 'inline-flex items-stretch *:data-[slot="button"]:relative *:data-[slot="select-trigger"]:relative [&>[data-slot="button"]:first-child]:z-10 [&>[data-slot="button"]:first-child:not(:last-child)]:rounded-r-none [&>[data-slot="button"]:last-child]:z-30 [&>[data-slot="button"]:last-child:not(:first-child)]:rounded-l-none [&>[data-slot="button"]:not(:first-child)]:-ml-px [&>[data-slot="button"]:not(:first-child):not(:last-child)]:z-20 [&>[data-slot="button"]:not(:first-child):not(:last-child)]:rounded-none [&>[data-slot="select-trigger"]:first-child]:z-10 [&>[data-slot="select-trigger"]:first-child:not(:last-child)]:rounded-r-none [&>[data-slot="select-trigger"]:last-child]:z-30 [&>[data-slot="select-trigger"]:last-child:not(:first-child)]:rounded-l-none [&>[data-slot="select-trigger"]:not(:first-child)]:-ml-px [&>[data-slot="select-trigger"]:not(:first-child):not(:last-child)]:z-20 [&>[data-slot="select-trigger"]:not(:first-child):not(:last-child)]:rounded-none',

  variants: {
    density: {
      // A primary action plus a trailing menu chevron: the label hugs the chevron, which shrinks to its icon.
      compact:
        '[&>[data-slot="button"]:first-child:not(:last-child)]:pr-1.5 [&>[data-slot="button"]:last-child:not(:first-child)]:aspect-auto [&>[data-slot="button"]:last-child:not(:first-child)]:w-auto [&>[data-slot="button"]:last-child:not(:first-child)]:px-1',
    },
  },
})

export interface SplitButtonGroupProps
  extends ComponentProps<'div'>, VariantProps<typeof splitButtonGroupVariants> {
  readonly children: ReactNode
}

/**
 * groups multiple inline buttons together inline and manages rounding so only outside corners are rounded
 */
export function SplitButtonGroup({
  children,
  className,
  density,
  ref,
  ...props
}: SplitButtonGroupProps) {
  return (
    <div
      {...props}
      ref={ref}
      className={splitButtonGroupVariants({ className, density })}
    >
      {children}
    </div>
  )
}

const rotatingChevronVariants = tv({
  base: 'relative top-px size-3 shrink-0 stroke-2 transition duration-200',

  defaultVariants: {
    orientation: 'vertical',
  },

  variants: {
    orientation: {
      horizontal:
        '-rotate-90 group-data-[state=open]:rotate-0 group-data-[state=open]/button:rotate-0',
      vertical:
        'group-data-[state=open]:rotate-180 group-data-[state=open]/button:rotate-180',
    },
  },
})

export function RotatingChevron({
  className,
  orientation,
  ref,
  ...iconProps
}: SVGProps<SVGSVGElement> & VariantProps<typeof rotatingChevronVariants>) {
  return (
    <ChevronDownIcon
      ref={ref}
      data-slot="rotating-chevron"
      className={cn(
        rotatingChevronVariants({
          orientation,
        }),
        className,
      )}
      aria-hidden="true"
      {...iconProps}
    />
  )
}

const buttonListVariants = tv({
  base: null,

  defaultVariants: {
    orientation: 'vertical',
  },

  slots: {
    item: null,
  },

  variants: {
    orientation: {
      horizontal: {
        base: 'flex w-full items-center gap-2',
        item: 'flex-[1_0_0]',
      },

      vertical: {
        base: null,
        item: '[&:not(:first-of-type)]:-my-px [&:not(:first-of-type)>button]:rounded-t-none [&:not(:last-of-type)>button]:rounded-b-none',
      },
    },
  },
})

const ButtonListContext =
  createNonNullableContext<VariantProps<typeof buttonListVariants>>(
    'ButtonListContext',
  )

export type ButtonListProps = ComponentProps<'ul'> &
  VariantProps<typeof buttonListVariants>

export function ButtonList({
  className,
  orientation = 'vertical',
  ...props
}: ButtonListProps) {
  const contextValue: VariantProps<typeof buttonListVariants> = {
    orientation,
  }

  const classNames = buttonListVariants({
    orientation,
  })

  return (
    <ButtonListContext.Context value={contextValue}>
      <ul className={cn(classNames.base(), className)} {...props} />
    </ButtonListContext.Context>
  )
}

export function ButtonListItem({
  children,
  className,
  ref,
  ...props
}: ComponentProps<'li'>) {
  const { orientation } = ButtonListContext.useRequiredContext()
  const classNames = buttonListVariants({
    orientation,
  })

  return (
    <li ref={ref} className={cn(classNames.item(), className)} {...props}>
      {children}
    </li>
  )
}

export const toggleInputButtonVariants = tv({
  base: 'w-full justify-start text-start',

  compoundVariants: [
    {
      className: 'bg-yellow-100 dark:bg-yellow-900/30',
      isSelected: true,
      variant: 'radio',
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'button',
  },

  slots: {
    container: 'flex flex-col items-start gap-y-0.5 font-normal',
    content: 'gap-x-1',
    subtitle: 'text-left text-xs text-tremor-content',
    title: 'text-left text-sm/tight font-medium',
  },
  variants: {
    isSelected: {
      true: {
        base: 'z-10',
      },
    },

    size: {
      lg: {
        base: 'py-3.5',
      },
      md: null,
    },

    variant: {
      button: null,
      checkbox: {},
      radio: {},
    },
  },
})

export interface ToggleInputButtonProps
  extends
    Omit<ButtonProps, 'isSelected' | 'size' | 'variant'>,
    Omit<VariantProps<typeof toggleInputButtonVariants>, 'isSelected'> {
  readonly children: ReactNode
  readonly isSelected: boolean
  readonly subtitle?: string
}

export function ToggleInputButton({
  children,
  isSelected,
  ref,
  size,
  subtitle,
  variant,
  ...buttonProps
}: ToggleInputButtonProps) {
  const classNames = toggleInputButtonVariants({
    isSelected,
    size,
    variant,
  })

  return (
    <Button
      ref={ref}
      role={variant === 'radio' ? 'radio' : 'switch'}
      variant={isSelected ? 'brand' : 'outline'}
      isSelected={isSelected}
      width="full"
      align="start"
      startIcon={
        variant === 'radio' ? (
          <RadioGroupRawIndicator isSelected={isSelected} />
        ) : variant === 'checkbox' ? (
          <CheckboxRawIndicator isSelected={isSelected} />
        ) : undefined
      }
      {...buttonProps}
    >
      <div className={cn(classNames.container(), size === 'lg' && 'py-1')}>
        <p
          className={cn(
            classNames.title(),
            subtitle === undefined && 'font-normal',
          )}
        >
          {children}
        </p>
        {subtitle === undefined ? null : (
          <p className={classNames.subtitle()}>{subtitle}</p>
        )}
      </div>
    </Button>
  )
}
